import React, { type FC } from "react";
import { SendOutlined } from "@ant-design/icons";
import { Button, Modal } from "../../../../shared";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { DISPLAY_LOCATION_OPTIONS, EMPTY_DISPLAY } from "../../constants";
import { ConfiguredBannerPreview } from "../../controls/BannerDesignField";
import { FreeformBannerPreview } from "../../controls/FreeformBannerEditor";
import { displayNameFor } from "../../eventCatalog";
import type { BannerIn, DisplayLocation, EventCategoryGroup } from "../../types";
import {
  DISPLAY_BANNER_CAMPAIGN_TYPE,
  DISPLAY_BANNER_RESULT_TYPE_KEY,
  formatScheduleDate,
  resolvedStepEventKeys,
  scheduleModeLabel,
  triggerSummaryLabel,
  type WizardFormValues,
  type WizardStepFormValue,
} from "./toWizardCampaignIn";
import styles from "../../stylesDynamicBonuses.module.css";

export type ActivateCampaignReviewModalProps = {
  open: boolean;
  values: WizardFormValues;
  eventLabel: string;
  eventGroups?: EventCategoryGroup[];
  loading?: boolean;
  onClose: () => void;
  onSaveAsDraft: () => void;
  onActivate: () => void;
};

const ReviewRow: FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className={styles.activateReviewRow}>
    <p className={styles.activateReviewRowLabel}>{label}</p>
    <p className={styles.activateReviewRowValue}>{value}</p>
  </div>
);

const displayLocationsSummary = (
  locations: DisplayLocation[] | undefined,
): string => {
  if (!locations?.length) {
    return AppStrings.DISPLAY_LOCATIONS_AUTOMATIC;
  }
  const labels = DISPLAY_LOCATION_OPTIONS().filter((option) =>
    locations.includes(option.value),
  ).map((option) => option.label);
  return labels.length > 0 ? labels.join(", ") : AppStrings.DISPLAY_LOCATIONS_AUTOMATIC;
};

const startsLabel = (values: WizardFormValues): string => {
  if (values.schedule_mode === "custom") {
    return formatScheduleDate(values.date_range?.[0]);
  }
  return AppStrings.PAM_DYNAMIC_BONUS_REVIEW_STARTS_IMMEDIATE;
};

const endsLabel = (values: WizardFormValues): string => {
  if (values.schedule_mode === "custom") {
    return formatScheduleDate(values.date_range?.[1]);
  }
  return scheduleModeLabel(values.schedule_mode) || EMPTY_DISPLAY;
};

const audienceLabel = (values: WizardFormValues): string =>
  values.audience_mode === "segment"
    ? AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_SEGMENT
    : AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_ALL_USERS;

const stepHasBanner = (step: WizardStepFormValue | undefined): step is WizardStepFormValue & {
  banner: BannerIn;
} => Boolean(step?.banner);

const ActivateCampaignReviewModal: FC<ActivateCampaignReviewModalProps> = ({
  open,
  values,
  eventLabel,
  eventGroups = [],
  loading = false,
  onClose,
  onSaveAsDraft,
  onActivate,
}) => {
  const isDisplayBannerCampaign =
    values.campaign_type === DISPLAY_BANNER_CAMPAIGN_TYPE;
  const steps = values.steps ?? [];
  const stepEventKeys = resolvedStepEventKeys(values);
  const uniqueStepEvents = new Set(stepEventKeys.filter(Boolean));
  const showPerStepEvents = uniqueStepEvents.size > 1;
  const bannerSteps = steps.flatMap((step, index) =>
    stepHasBanner(step) ? [{ step, index }] : [],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_ACTIVATE_TITLE}
      size="lg"
      className={styles.activateReviewModal}
      destroyOnClose
      footer={
        <div className={styles.activateReviewFooter}>
          <Button
            intent="neutral"
            variant="outline"
            color="var(--grey-600, #434350)"
            disabled={loading}
            onClick={onSaveAsDraft}
          >
            {AppStrings.PAM_DYNAMIC_BONUS_SAVE_AS_DRAFT}
          </Button>
          <Button
            intent="primary"
            iconLeft={<SendOutlined />}
            loading={loading}
            disabled={loading}
            onClick={onActivate}
          >
            {AppStrings.PAM_DYNAMIC_BONUS_ACTIVATE_CAMPAIGN}
          </Button>
        </div>
      }
    >
      <div className={styles.activateReviewBody}>
        <ReviewRow
          label={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_NAME_LABEL}
          value={values.name?.trim() || EMPTY_DISPLAY}
        />
        <ReviewRow
          label={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_STARTS_LABEL}
          value={startsLabel(values)}
        />
        <ReviewRow
          label={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_ENDS_LABEL}
          value={endsLabel(values)}
        />
        <ReviewRow
          label={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_AUDIENCE_LABEL}
          value={audienceLabel(values)}
        />
        {!isDisplayBannerCampaign && (
          <ReviewRow
            label={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_TRIGGER_LABEL}
            value={triggerSummaryLabel(values.trigger_mode, eventLabel)}
          />
        )}
        {showPerStepEvents &&
          stepEventKeys.map((eventKey, index) =>
            eventKey ? (
              <ReviewRow
                key={`step-event-${index}`}
                label={formatString(AppStrings.PAM_DYNAMIC_BONUS_REVIEW_STEP_EVENT, {
                  n: String(index + 1),
                })}
                value={displayNameFor(eventGroups, eventKey) || eventKey}
              />
            ) : null,
          )}
        {values.trigger_mode === "affiliate_referral" && (
          <ReviewRow
            label={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_AFFILIATE_CODE_LABEL}
            value={values.affiliate_referral_code?.trim() || EMPTY_DISPLAY}
          />
        )}
        {!isDisplayBannerCampaign && (
          <ReviewRow
            label={AppStrings.PAM_DYNAMIC_BONUS_REVIEW_STEPS_LABEL}
            value={String(steps.length)}
          />
        )}

        {bannerSteps.map(({ step, index }, bannerIndex) => {
          const isDisplayBannerResult =
            isDisplayBannerCampaign ||
            step.result_type_key === DISPLAY_BANNER_RESULT_TYPE_KEY;
          return (
          <div
            key={`banner-${index}`}
            className={styles.activateReviewBannerBlock}
            data-testid={`activate-review-banner-${bannerIndex + 1}`}
          >
            <ReviewRow
              label={
                isDisplayBannerResult
                  ? AppStrings.DISPLAY_LOCATIONS
                  : formatString(AppStrings.PAM_DYNAMIC_BONUS_REVIEW_BANNER_LABEL, {
                      n: String(bannerIndex + 1),
                    })
              }
              value={displayLocationsSummary(step.display_locations)}
            />
            <div className={styles.bannerDesignPreview}>
              {isDisplayBannerResult ? (
                <FreeformBannerPreview
                  banner={step.banner}
                  layout={step.freeform_layout}
                />
              ) : (
                <ConfiguredBannerPreview banner={step.banner} />
              )}
            </div>
          </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ActivateCampaignReviewModal;
